import { oauthAuth } from "../standardAuthPatterns";
import { defineProduct, type FieldTarget, productVariant } from "../types";
import logo from "./logo.svg";

const defaultUrlPattern = () => "https://[^/]*\\.egnyte\\.com/.*";

const instanceStep = {
  title: "Enter your Egnyte instance details",
  items: [
    {
      kind: "markdown",
      content: "Enter the details for your Egnyte instance.",
    },
    {
      kind: "input",
      label: "Egnyte Domain",
      placeholder: "domain",
      required: true,
      target: { kind: "configuration", field: "subdomain" },
      helperText:
        "The subdomain of your Egnyte account (e.g. https://domain.egnyte.com)",
    },
  ],
} as const;

const apiKeyGuidance =
  "Go to [developers.egnyte.com](https://developers.egnyte.com/member/register) and register a developer account if you haven’t already. Then create a new application under **My Apps**, choosing **Internal Application** if it’s for your own Egnyte domain. New keys require approval from Egnyte (typically within one business day) and default to 2 requests per second and 1,000 calls per day.";

const credentialsStep = {
  title: "Enter your app credentials",
  items: [
    {
      kind: "input",
      label: "Client ID",
      required: true,
      target: { kind: "oauthConnection", field: "clientId" },
    },
    {
      kind: "input",
      label: "Client secret",
      required: true,
      target: { kind: "oauthConnection", field: "clientSecret" },
    },
  ],
} as const;

const scopesStep = {
  title: "Define scopes for this connection",
  items: [
    {
      kind: "markdown",
      content:
        "Add [scopes](https://developers.egnyte.com/api-docs/read/public-api-authentication) as a space-separated list below. `Egnyte.filesystem` is enough for file and folder operations.",
    },
    {
      kind: "input",
      label: "Scopes",
      placeholder: "Egnyte.filesystem Egnyte.link",
      required: true,
      defaultValue: "Egnyte.filesystem",
      helperText: "Space-separated list of scopes.",
      target: { kind: "oauthConnection", field: "scopes" },
    },
  ],
} as const;

const oauthDependencies: FieldTarget[] = [
  { kind: "oauthConnection", field: "clientId" },
  { kind: "oauthConnection", field: "clientSecret" },
  { kind: "oauthConnection", field: "scopes" },
];

const oauthEndpoints = {
  authorizationEndpoint: ({ subdomain }: Record<string, string>) =>
    `https://${subdomain}.egnyte.com/puboauth/token`,
  tokenEndpoint: ({ subdomain }: Record<string, string>) =>
    `https://${subdomain}.egnyte.com/puboauth/token`,
  tokenEndpointAuthMethod: () => "client_secret_post" as const,
  clientCredentialsFrom: "db" as const,
  scopes: () => ["Egnyte.filesystem"],
};

const connectionTest = {
  protocol: "http",
  request: ({ subdomain }: Record<string, string>) =>
    `https://${subdomain}.egnyte.com/pubapi/v1/userinfo`,
} as const;

const publicEnvVars = ({ subdomain }: Record<string, string>) => ({
  EGNYTE_URL: subdomain ? `https://${subdomain}.egnyte.com` : "",
});

export const egnyte = defineProduct({
  name: "Egnyte",
  tagLine: "Enterprise file sharing and governance",
  logo,
  tags: ["data"],
  variants: [
    productVariant({
      identifier: "egnyte",
      authLabel: "User account",
      protocol: "http",
      defaultUrlPattern,
      auth: oauthAuth,
      steps: [
        instanceStep,
        {
          title: "Create an Egnyte API key",
          items: [{ kind: "markdown", content: apiKeyGuidance }],
        },
        {
          title: "Configure the redirect URI",
          items: [
            {
              kind: "markdown",
              content:
                "In your app’s settings, add the following redirect URI:",
            },
            {
              kind: "copyable",
              label: "Redirect URI",
              value: "{API_URL}/connected-apps/oauth/egnyte/callback",
            },
          ],
        },
        credentialsStep,
        scopesStep,
        {
          title: "Authorize your Egnyte account",
          items: [
            {
              kind: "markdown",
              content: "Click below to authorize your Egnyte account.",
            },
            { kind: "oauth", dependsOn: oauthDependencies },
          ],
        },
      ],
      connectionTest,
      publicEnvVars,
      oauth: { ...oauthEndpoints, grantType: () => "authorization_code" },
    }),
    productVariant({
      identifier: "egnyteServiceAccount",
      authLabel: "Service account",
      protocol: "http",
      defaultUrlPattern,
      auth: oauthAuth,
      steps: [
        instanceStep,
        {
          title: "Create an Egnyte API key",
          items: [
            { kind: "markdown", content: apiKeyGuidance },
            {
              kind: "markdown",
              content:
                "Request the key while signed in as a domain administrator — Egnyte declines API key requests from non-admins and from service accounts. This connection type needs no redirect URI.",
            },
          ],
        },
        credentialsStep,
        scopesStep,
        {
          title: "Sign in as your Egnyte service account",
          items: [
            {
              kind: "markdown",
              content:
                "Enter the credentials of the Egnyte service account this connector should act as; its permissions decide which folders 3B can reach. Service accounts can’t complete an interactive browser sign-in, so 3B exchanges these credentials for a token directly. They are used once and never stored.",
            },
            { kind: "oauth", dependsOn: oauthDependencies },
          ],
        },
      ],
      connectionTest,
      publicEnvVars,
      oauth: { ...oauthEndpoints, grantType: () => "password" },
    }),
  ],
});
